import { useEffect, useState } from 'react';
import {
  Typography,
  CardContent,
  Box,
  Button,
  Tooltip,
} from '@material-ui/core';
import Stack from '@mui/material/Stack';
import { InfoCard, Link } from '@backstage/core-components';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { parseEntityRef } from '@backstage/catalog-model';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  identityApiRef,
  ProfileInfo,
  useApi,
} from '@backstage/core-plugin-api';
import { useRouteRef } from '@backstage/frontend-plugin-api';

const SignOutButton = () => {
  const identityApi = useApi(identityApiRef);

  return (
    <Tooltip title="Sign out from Backstage" placement="top" arrow>
      <Button
        size="medium"
        variant="outlined"
        color="primary"
        onClick={() => identityApi.signOut()}
        aria-label="Sign out from Backstage"
      >
        Sign out
      </Button>
    </Tooltip>
  );
};

export const SettingsUserIdentityCard = () => {
  const identityApi = useApi(identityApiRef);
  const catalogRouteRef = useRouteRef(entityRouteRef);
  const [userData, setUserData] = useState<ProfileInfo | null>(null);
  const [userEntityRef, setUserEntityRef] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const [profileInfo, backstageIdentity] = await Promise.all([
          identityApi.getProfileInfo(),
          identityApi.getBackstageIdentity(),
        ]);
        setUserData(profileInfo);
        setUserEntityRef(backstageIdentity.userEntityRef);
      } catch (err) {
        setError(`Could not fetch your user identity: ${err}`);
      }
    };

    fetchUserInfo();
  }, [identityApi]);

  return (
    <InfoCard title="Identity">
      <CardContent>
        {error || !userData ? (
          <Typography>Could not fetch your user identity</Typography>
        ) : (
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={2}>
              <UserSettingsSignInAvatar size={100} />
              <Stack direction="column" spacing={1}>
                <Box>
                  <Typography variant="h5">{userData.displayName}</Typography>
                  <Typography>{userData.email}</Typography>
                </Box>
                {catalogRouteRef && userEntityRef && (
                  <Link
                    to={catalogRouteRef(parseEntityRef(userEntityRef))}
                    variant="button"
                  >
                    Your Backstage Profile
                  </Link>
                )}
              </Stack>
            </Stack>
            <Stack alignSelf="center">
              <SignOutButton />
            </Stack>
          </Stack>
        )}
      </CardContent>
    </InfoCard>
  );
};
